import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { store, load, clear, K_PLAYER } from "../lib/storage";
import { DigLogo } from "../components/dig/DigLogo";
import { GameCard, CardBack } from "../components/dig/GameCard";
import { ExampleOffer } from "../components/dig/ExampleOffer";
import { LANGS } from "../lib/i18n";
import { useCountdown } from "../lib/useRoom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Layers, Clock, Sparkles, Volume2, VolumeX } from "lucide-react";
import { playShuffle, playFlip, isMuted, setMuted as sfxSetMuted } from "../lib/sfx";

export default function Player() {
  const saved = load(K_PLAYER);
  const [playerId, setPlayerId] = useState(saved?.playerId || null);
  const [state, setState] = useState(null);
  const [lang, setLang] = useState("es");
  const [muted, setMutedState] = useState(isMuted());
  const toggleMute = () => { const v = !muted; sfxSetMuted(v); setMutedState(v); };
  const [step, setStep] = useState(saved?.step || "deal"); // deal -> solo -> ready

  // QR/code join: prefill code from ?code= query param
  const params = new URLSearchParams(window.location.search);
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [name, setName] = useState("");
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState("");

  const poll = useCallback(async () => {
    if (!playerId) return;
    try {
      const { data } = await api.get(`/player/${playerId}`);
      setState(data);
    } catch (e) {
      if (e?.response?.status === 404) { clear(K_PLAYER); setPlayerId(null); }
    }
  }, [playerId]);

  useEffect(() => {
    if (!playerId) return;
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [playerId, poll]);

  const persistStep = (s) => { setStep(s); store(K_PLAYER, { ...(load(K_PLAYER) || {}), step: s }); };

  const join = async () => {
    setErr("");
    if (!code.trim()) { setErr("Escribe el código de la sala."); return; }
    if (!name.trim()) { setErr("Escribe tu nombre."); return; }
    setJoining(true);
    try {
      const { data } = await api.post(`/session/${code.trim().toUpperCase()}/player`, { name: name.trim() });
      store(K_PLAYER, { playerId: data.id, code: code.trim().toUpperCase(), step: "deal" });
      setPlayerId(data.id);
      setStep("deal");
      toast.success(`Hola ${data.display_name}`);
    } catch (e) {
      const msg = e?.response?.status === 404 ? "Sala no encontrada. Pide el código al facilitador." : "No se pudo unir. Inténtalo de nuevo.";
      setErr(msg); toast.error(msg);
    } finally { setJoining(false); }
  };

  /* ---------- JOIN (QR / code + name) ---------- */
  if (!playerId) {
    return (
      <div className="min-h-screen bg-dig-coral flex flex-col items-center justify-center px-6 text-white">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-3xl p-8 dig-card-shadow text-dig-harbour">
            <div className="flex justify-center mb-6"><DigLogo /></div>
            <h1 className="text-2xl font-bold text-center mb-1">Únete al juego</h1>
            <p className="text-center text-sm text-dig-harbour/60 mb-6">Escanea el QR o escribe el código de la sala</p>
            <label className="text-xs font-semibold tracking-wide text-dig-harbour/60">CÓDIGO DE SALA</label>
            <Input data-testid="player-code-input" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(""); }}
              placeholder="ABC12" maxLength={6}
              className="mt-1 mb-4 text-center text-2xl font-mono-dig tracking-[0.3em] h-14" />
            <label className="text-xs font-semibold tracking-wide text-dig-harbour/60">TU NOMBRE</label>
            <Input data-testid="player-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ana" className="mt-1 mb-4 h-12" />
            {err ? <div data-testid="player-join-error" className="mb-4 rounded-xl bg-dig-coral-tint text-dig-coral text-sm font-semibold px-4 py-3">{err}</div> : null}
            <Button data-testid="player-join-btn" onClick={join} disabled={joining}
              className="w-full h-12 rounded-full bg-dig-coral hover:bg-dig-coral/90 text-white font-bold text-base">
              {joining ? <Loader2 className="animate-spin" /> : "Entrar"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return <div className="min-h-screen bg-dig-paper flex items-center justify-center"><Loader2 className="animate-spin text-dig-blue" size={40} /></div>;
  }

  const p = state.player;
  const phase = state.phase;
  const isPitch = phase === "pitch";
  const isCierre = phase === "cierre";

  return (
    <div className="min-h-screen bg-dig-paper text-dig-harbour flex flex-col">
      <header className="sticky top-0 z-20 bg-dig-harbour text-white px-5 py-3 flex items-center justify-between">
        <div>
          <div className="font-bold leading-none">{p.display_name}</div>
          <div className="text-[0.6rem] text-white/50 tracking-widest mt-0.5">SALA {p.session_code}</div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="player-mute-toggle" onClick={toggleMute} title={muted ? "Unmute" : "Mute"}
            className="text-white/70 hover:text-white">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
          <div className="flex bg-white/10 rounded-full p-0.5">
            {LANGS.map((l) => (
              <button key={l.key} data-testid={`player-lang-${l.key}`} onClick={() => setLang(l.key)}
                className={`text-[11px] font-bold rounded-full px-2 py-1 ${lang === l.key ? "bg-white text-dig-harbour" : "text-white/70"}`}>{l.label}</button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        {isPitch ? (
          <div className="px-5 py-6 space-y-6">
            <BuyerQuestions questions={state.buyer_questions} lang={lang} />
            <InvestPanel playerId={playerId} teams={state.teams} budget={state.invest_budget} investments={state.investments} lang={lang} />
          </div>
        ) : isCierre ? (
          <div className="px-5 py-6 space-y-6">
            <InvestPanel playerId={playerId} teams={state.teams} budget={state.invest_budget} investments={state.investments} lang={lang} />
            <Commitment tpl={state.commitment_template} lang={lang} />
          </div>
        ) : step === "deal" ? (
          <DealChoose hand={p.hand} lang={lang} onDone={() => persistStep("example")} />
        ) : step === "example" ? (
          <div className="flex-1 px-5 py-6 flex flex-col">
            <ExampleOffer lang={lang} />
            <Button data-testid="example-continue-btn" onClick={() => persistStep("solo")}
              className="mt-6 h-14 rounded-full bg-dig-coral hover:bg-dig-coral/90 text-white font-bold text-lg">
              {lang === "en" ? "Got it — my turn" : lang === "both" ? "Entendido · Got it" : "Entendido, mi turno"}
            </Button>
          </div>
        ) : step === "solo" ? (
          <SoloRound hand={p.hand} lang={lang} onDone={() => persistStep("ready")} />
        ) : (
          <Ready hand={p.hand} lang={lang} onReplay={() => persistStep("solo")} />
        )}
      </main>
    </div>
  );
}

/* ---------- Stage 6 · Invest: back your favourite idea ---------- */
function InvestPanel({ playerId, teams = [], budget = 1000, investments = {}, lang }) {
  const [alloc, setAlloc] = useState(() => ({ ...investments }));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const spent = Object.values(alloc).reduce((a, b) => a + (Number(b) || 0), 0);
  const remaining = budget - spent;
  const step = 100;
  const bump = (tid, d) => {
    setSaved(false);
    setAlloc((a) => {
      const cur = Number(a[tid]) || 0;
      let next = cur + d;
      if (next < 0) next = 0;
      if (d > 0 && remaining <= 0) return a;
      if (d > 0 && next - cur > remaining) next = cur + remaining;
      const copy = { ...a }; if (next === 0) delete copy[tid]; else copy[tid] = next; return copy;
    });
  };
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/player/${playerId}/invest`, { allocations: alloc }); setSaved(true); toast.success(lang === "es" ? "¡Inversión enviada!" : "Investment sent!"); }
    catch (e) { toast.error(e?.response?.data?.detail || (lang === "es" ? "Error" : "Error")); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <span className="inline-block bg-dig-turquoise text-dig-harbour text-xs font-bold tracking-widest rounded-full px-3 py-1 mb-3">
        {lang === "es" ? "INVIERTE TU DINERO" : "INVEST YOUR MONEY"}
      </span>
      <h2 className="text-2xl font-bold mb-1">{lang === "es" ? "Apoya tu idea favorita" : "Back your favourite idea"}</h2>
      <p className="text-sm text-dig-harbour/60 mb-3">{lang === "es" ? "Reparte tu dinero entre los equipos. No inviertas en el tuyo." : "Split your money across the teams. Don't invest in your own."}</p>
      <div className="sticky top-14 z-10 bg-dig-paper py-2 mb-2">
        <div className="flex items-center justify-between bg-dig-harbour text-white rounded-xl px-4 py-2">
          <span className="text-xs tracking-widest text-white/60">{lang === "es" ? "DISPONIBLE" : "AVAILABLE"}</span>
          <span data-testid="invest-remaining" className={`font-mono-dig text-2xl font-bold ${remaining <= 0 ? "text-dig-coral" : "text-dig-yellow"}`}>${remaining}</span>
        </div>
      </div>
      <div className="space-y-2.5">
        {teams.map((t) => {
          const v = Number(alloc[t.id]) || 0;
          return (
            <div key={t.id} data-testid={`invest-team-${t.id}`} className="bg-white rounded-2xl border border-border p-3 flex items-center gap-3">
              <span className="w-4 h-4 rounded-full flex-none" style={{ background: t.color_hex }} />
              <span className="font-semibold flex-1 truncate">{t.name}</span>
              <button data-testid={`invest-minus-${t.id}`} onClick={() => bump(t.id, -step)} disabled={v <= 0}
                className="w-9 h-9 rounded-full bg-dig-harbour-tint text-dig-harbour font-bold text-lg disabled:opacity-40">–</button>
              <span data-testid={`invest-amount-${t.id}`} className="font-mono-dig font-bold w-16 text-center">${v}</span>
              <button data-testid={`invest-plus-${t.id}`} onClick={() => bump(t.id, step)} disabled={remaining <= 0}
                className="w-9 h-9 rounded-full bg-dig-turquoise text-dig-harbour font-bold text-lg disabled:opacity-40">+</button>
            </div>
          );
        })}
        {teams.length === 0 ? <p className="text-sm text-dig-harbour/50">{lang === "es" ? "Aún no hay equipos." : "No teams yet."}</p> : null}
      </div>
      <Button data-testid="invest-submit-btn" onClick={submit} disabled={busy || spent === 0}
        className="mt-4 w-full h-12 rounded-full bg-dig-coral text-white font-bold">
        {busy ? <Loader2 className="animate-spin" /> : saved ? (lang === "es" ? "✓ Actualizar inversión" : "✓ Update investment") : (lang === "es" ? "Enviar inversión" : "Send investment")}
      </Button>
    </div>
  );
}

/* ---------- Stage 2 · Deal: tap to shuffle, then tap each card to choose ---------- */
function FlipCard({ card, revealed, onReveal, lang, w = 150, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6, y: -40 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 220, damping: 20 }}
      style={{ perspective: 1000, width: w, cursor: revealed ? "default" : "pointer" }}
      onClick={() => !revealed && onReveal()}
      data-testid={`flip-card-${card.slot}`}
    >
      <motion.div
        animate={{ rotateY: revealed ? 180 : 0, y: revealed ? 0 : [0, -6, 0] }}
        transition={{ rotateY: { duration: 0.5, ease: [0.2, 0.8, 0.3, 1] }, y: { duration: 1.6, repeat: revealed ? 0 : Infinity, ease: "easeInOut" } }}
        style={{ position: "relative", transformStyle: "preserve-3d", width: w, aspectRatio: "63 / 88" }}
      >
        <div style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}>
          <CardBack width={w} />
        </div>
        <div style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
          <GameCard category={card.slot} code={card.card_code} es={card.es} en={card.en} isBlank={card.is_blank} blankText={card.blank_text} width={w} lang={lang} />
        </div>
      </motion.div>
    </motion.div>
  );
}

function DealChoose({ hand, lang, onDone }) {
  const [phase, setPhase] = useState("stack"); // stack -> shuffling -> reveal
  const [revealed, setRevealed] = useState([]);
  const w = 150;
  const cards = hand || [];
  const allRevealed = cards.length > 0 && revealed.length >= cards.length;

  const shuffle = () => {
    if (phase !== "stack") return;
    playShuffle();
    setPhase("shuffling");
    setTimeout(() => setPhase("reveal"), 950);
  };
  const reveal = (slot) => setRevealed((r) => (r.includes(slot) ? r : (playFlip(), [...r, slot])));

  return (
    <div className="flex-1 px-5 py-6 flex flex-col">
      <span className="inline-block bg-dig-blue text-white text-xs font-bold tracking-widest rounded-full px-3 py-1 mb-2 w-fit">
        {lang === "es" ? "ETAPA 2 · EL REPARTO" : "STAGE 2 · THE DEAL"}
      </span>

      {phase !== "reveal" ? (
        <>
          <h2 className="text-2xl font-bold mb-1">{lang === "es" ? "Baraja tus cartas" : "Shuffle your cards"}</h2>
          <p className="text-sm text-dig-harbour/60 mb-8">{lang === "es" ? "Toca la baraja para barajar y repartir tu mano." : "Tap the deck to shuffle and deal your hand."}</p>
          <div className="flex-1 flex flex-col items-center justify-center">
            <div onClick={shuffle} data-testid="deal-shuffle-deck" style={{ position: "relative", width: w, height: (w * 88) / 63, cursor: "pointer" }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.div key={i} style={{ position: "absolute", inset: 0 }}
                  animate={phase === "shuffling"
                    ? { x: [0, (i % 2 ? 1 : -1) * (26 + i * 4), (i % 2 ? -1 : 1) * 10, 0], rotate: [0, (i % 2 ? 10 : -10), 0], y: [0, -12 - i * 2, 0] }
                    : { x: i * 1.5, y: -i * 1.5, rotate: (i - 2) * 2.2 }}
                  transition={phase === "shuffling" ? { duration: 0.85, ease: "easeInOut" } : { type: "spring", stiffness: 200, damping: 20 }}
                >
                  <CardBack width={w} />
                </motion.div>
              ))}
            </div>
            <Button data-testid="deal-shuffle-btn" onClick={shuffle} disabled={phase === "shuffling"}
              className="mt-10 h-14 px-10 rounded-full bg-dig-coral hover:bg-dig-coral/90 text-white font-bold text-lg">
              {phase === "shuffling"
                ? (lang === "es" ? "Barajando…" : "Shuffling…")
                : (lang === "es" ? "Barajar y repartir" : "Shuffle & deal")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-bold mb-1">{lang === "es" ? "Elige tus cartas" : "Choose your cards"}</h2>
          <p className="text-sm text-dig-harbour/60 mb-5">
            {allRevealed
              ? (lang === "es" ? "Esta es tu mano. Llévala a tu equipo." : "This is your hand. Bring it to your team.")
              : (lang === "es" ? "Toca cada carta para darle la vuelta." : "Tap each card to flip it over.")}
          </p>
          <div className="grid grid-cols-2 gap-5 place-items-center flex-1 content-center">
            {cards.map((c, i) => (
              <FlipCard key={c.slot} card={c} w={w} delay={i * 0.12} lang={lang}
                revealed={revealed.includes(c.slot)} onReveal={() => reveal(c.slot)} />
            ))}
          </div>
          <AnimatePresence>
            {allRevealed ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <Button data-testid="player-deal-continue" onClick={onDone}
                  className="mt-6 w-full h-14 rounded-full bg-dig-coral hover:bg-dig-coral/90 text-white font-bold text-lg">
                  {lang === "es" ? "Estoy listo para mi pitch" : "I'm ready to pitch"}
                </Button>
              </motion.div>
            ) : (
              <button data-testid="deal-reveal-all" onClick={() => { playFlip(); setRevealed(cards.map((c) => c.slot)); }}
                className="mt-6 mx-auto text-sm text-dig-harbour/50 underline">
                {lang === "es" ? "Voltear todas" : "Flip all"}
              </button>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

/* ---------- Stage 2 · Deal: the 90-second solo pitch ---------- */
function SoloRound({ hand, lang, onDone }) {
  const [started, setStarted] = useState(false);
  const [left, setLeft] = useState(90);
  const ref = useRef(null);

  useEffect(() => {
    if (!started) return;
    ref.current = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => clearInterval(ref.current);
  }, [started]);

  useEffect(() => { if (started && left === 0) clearInterval(ref.current); }, [started, left]);

  const warn = left <= 10 && left > 0;

  return (
    <div className="flex-1 px-5 py-6 flex flex-col">
      <span className="inline-block bg-dig-coral text-white text-xs font-bold tracking-widest rounded-full px-3 py-1 mb-2 w-fit">
        {lang === "es" ? "ETAPA 2 · 90 SEGUNDOS" : "STAGE 2 · 90 SECONDS"}
      </span>
      <h2 className="text-2xl font-bold mb-2">{lang === "es" ? "Vende tu mano" : "Pitch your hand"}</h2>
      <p className="text-base text-dig-harbour/70 leading-relaxed mb-5">
        {lang === "es"
          ? "Juega la mano que te tocó y preséntala en 90 segundos, por absurda que sea. Sin lienzo, sin escribir, sin juicios."
          : "Play the hand you were dealt and pitch it in ninety seconds, however absurd. No canvas, no writing, no judgement."}
      </p>

      <div className="flex flex-col items-center justify-center mb-6">
        <div data-testid="solo-timer" className={`font-mono-dig font-bold ${warn ? "text-dig-coral dig-pulse" : left === 0 ? "text-dig-turquoise" : "text-dig-harbour"}`}
          style={{ fontSize: "clamp(3.5rem, 22vw, 7rem)", lineHeight: 1 }}>
          {String(Math.floor(left / 60)).padStart(2, "0")}:{String(left % 60).padStart(2, "0")}
        </div>
        {!started ? (
          <Button data-testid="solo-start-btn" onClick={() => setStarted(true)}
            className="mt-4 h-12 px-8 rounded-full bg-dig-coral text-white font-bold"><Clock size={18} className="mr-2" />{lang === "es" ? "Empezar 90s" : "Start 90s"}</Button>
        ) : left === 0 ? (
          <Button data-testid="solo-done-btn" onClick={onDone}
            className="mt-4 h-12 px-8 rounded-full bg-dig-turquoise text-dig-harbour font-bold"><Sparkles size={18} className="mr-2" />{lang === "es" ? "¡Listo! Guardar mano" : "Done! Keep my hand"}</Button>
        ) : (
          <button data-testid="solo-skip-btn" onClick={onDone} className="mt-4 text-sm text-dig-harbour/50 underline">{lang === "es" ? "Terminé antes" : "I finished early"}</button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {(hand || []).map((c) => (
          <div key={c.slot} className="flex justify-center"><GameCard category={c.slot} code={c.card_code} es={c.es} en={c.en} size="sm" lang={lang} /></div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Cards persist on phone ---------- */
function Ready({ hand, lang, onReplay }) {
  return (
    <div className="flex-1 px-5 py-6 flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold">{lang === "es" ? "Tus cartas" : "Your cards"}</h2>
        <button data-testid="solo-replay-btn" onClick={onReplay} className="text-xs font-semibold text-dig-blue underline flex items-center gap-1"><Clock size={13} />{lang === "es" ? "Repetir 90s" : "Replay 90s"}</button>
      </div>
      <p className="text-sm text-dig-harbour/60 mb-5">{lang === "es" ? "Tu mano queda aquí. Llévala a la mesa de tu equipo." : "Your hand stays here. Bring it to your team table."}</p>
      <div className="grid grid-cols-2 gap-4">
        {(hand || []).map((c, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="flex justify-center">
            <GameCard category={c.slot} code={c.card_code} es={c.es} en={c.en} size="md" lang={lang} />
          </motion.div>
        ))}
      </div>
      <div className="mt-6 flex items-center gap-2 text-dig-turquoise font-semibold justify-center">
        <Layers size={16} /> {lang === "es" ? "Mano guardada" : "Hand saved"}
      </div>
    </div>
  );
}

/* ---------- Stage 5 · Pitch: buyer questions ---------- */
function BuyerQuestions({ questions, lang }) {
  return (
    <div>
      <span className="inline-block bg-dig-coral text-white text-xs font-bold tracking-widest rounded-full px-3 py-1 mb-3">
        {lang === "es" ? "ERES EL COMPRADOR" : "YOU ARE THE BUYER"}
      </span>
      <h2 className="text-2xl font-bold mb-1">{lang === "es" ? "Pregunta esto" : "Ask these"}</h2>
      <p className="text-sm text-dig-harbour/60 mb-5">
        {lang === "es" ? "Durante el pitch, presiona al equipo con estas preguntas." : "During the pitch, press the team with these questions."}
      </p>
      <div className="space-y-3">
        {questions.map((q, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
            className="bg-white rounded-2xl p-5 dig-card-shadow flex gap-4 items-start" data-testid={`buyer-q-${i}`}>
            <div className="w-8 h-8 rounded-full bg-dig-coral text-white font-bold flex items-center justify-center shrink-0">{i + 1}</div>
            <div>
              <p className="font-semibold text-lg leading-snug">{lang === "es" ? q.es : q.en}</p>
              <p className="text-xs text-dig-harbour/50 mt-1">{lang === "es" ? q.en : q.es}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Stage 6 · Commit ---------- */
function Commitment({ tpl, lang }) {
  return (
    <div>
      <span className="inline-block bg-dig-blue text-white text-xs font-bold tracking-widest rounded-full px-3 py-1 mb-3">
        {lang === "es" ? "COMPROMISO" : "COMMITMENT"}
      </span>
      <h2 className="text-2xl font-bold mb-4">{lang === "es" ? "Tu compromiso" : "Your commitment"}</h2>
      <div className="bg-white rounded-2xl p-6 dig-card-shadow">
        <p className="text-lg leading-relaxed font-medium">{lang === "es" ? tpl.es : tpl.en}</p>
      </div>
      <p className="text-sm text-dig-harbour/60 mt-4">
        {lang === "es" ? "El facilitador tomará una foto de tu tarjeta de compromiso." : "The facilitator will photograph your commitment card."}
      </p>
    </div>
  );
}
